<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'InvoicingProviderSettings',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    item: {
      type: Object,
      required: true,
    },
    saving: {
      type: Function,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const formId = ref(null);
  const formName = ref('InvoicingProviderSettings');
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'lg',
  }));

  const $data = computed(() => ({
    formId: formId.value,
    formName: formName.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_formId = () => {
    return $fn.value.randomElementId();
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'formId' },
      { name: 'formName' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: { formId: $$load_formId },
    ux: {},
    dataRefs: {
      formId,
      formName,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: [
        $fn.value.pgettext(
          'InvoicingProviderSettings|Title',
          'Invoicing provider configuration'
        ),
      ],
      icon: $config.value.settings.icon,
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.item,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('item');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_0jon96i5k_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_script_bhklkc94z_value_callable = async (
    $event
  ) => {
    return props
      .saving(props.item)
      .then((result) =>
        $imodal.value.close(props.modalName, { ok: true, value: result })
      );
  };
  const act_1040167445267876_script_bhklkc94z_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_1040167445267876_script_bhklkc94z_value_callable($event);
      },
    };
  };
  const act_2434514588044777_closeModal_6456bwk9s_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
    };
  };

  const act_7315092382398562_closeModal_0jon96i5k =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_0jon96i5k_getActionArgs,
    });
  const inst_evh_7315092382398562_close_egpu950dp =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_0jon96i5k,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_egpu950dp],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_ez6hpjg3y =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_pnfnry1mq =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_ez6hpjg3y,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_pnfnry1mq],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_630zubpfd =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_61siy3c7z =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_630zubpfd,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_61siy3c7z],
    event: 'close',
  });
  const act_1040167445267876_script_bhklkc94z = new actionTypes.ScriptAction({
    actionArgs: act_1040167445267876_script_bhklkc94z_getActionArgs,
    displayName: 'submit',
  });
  const inst_evh_1040167445267876_submit_c5p124dt3 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_script_bhklkc94z,
      event: 'submit',
      displayName: 'submit',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_c5p124dt3],
    event: 'submit',
  });
  const act_2434514588044777_closeModal_6456bwk9s =
    new actionTypes.CloseModalAction({
      actionArgs: act_2434514588044777_closeModal_6456bwk9s_getActionArgs,
    });
  const inst_evh_2434514588044777_clickPreventStop_saudmu3hp =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeModal_6456bwk9s,
      event: 'click.prevent.stop',
      displayName: 'closeModal',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_saudmu3hp],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_egpu950dp,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_pnfnry1mq,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_61siy3c7z,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_submit_c5p124dt3,
    submit: inst_evh_1040167445267876_submit_c5p124dt3,
    inst_evh_1040167445267876_submit,
    inst_evh_2434514588044777_clickPreventStop_saudmu3hp,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_egpu950dp = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_egpu950dp.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_pnfnry1mq = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_pnfnry1mq.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_61siy3c7z = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_61siy3c7z.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_c5p124dt3 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_c5p124dt3.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_saudmu3hp = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_saudmu3hp.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="InvoicingProviderSettings"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :name="formName"
        :labelAlign="$config.forms.defaultLabelAlign"
        :id="formId"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpRow>
          <ItpCol>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="name"
              :label="pgettext('InvoicingProviderSettings', 'Name')"
              required
            >
              <BFormInput
                plaintext
                v-model="item.name"
                class="fw-bold no-focus-border"
              >
              </BFormInput>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="url"
              :label="pgettext('InvoicingProviderSettings', 'URL')"
              :helpTooltip="
                pgettext(
                  'InvoicingProviderSettings',
                  'The root URL of the service.'
                )
              "
              required
            >
              <ItpFormInput name="url" v-model="item.url" required>
              </ItpFormInput>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="invoice_prefix"
              :label="pgettext('InvoicingProviderSettings', 'Invoice prefix')"
              required
              :helpTooltip="
                pgettext(
                  'InvoicingProviderSettings',
                  'The invoice prefix set up by the provider.'
                )
              "
            >
              <ItpFormInput
                name="invoice_prefix"
                v-model="item.invoice_prefix"
                required
              >
              </ItpFormInput>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="username"
              :label="pgettext('InvoicingProviderSettings', 'Username')"
              :helpTooltip="
                pgettext(
                  'InvoicingProviderSettings',
                  'The technical user\'s name.'
                )
              "
              required
            >
              <ItpFormInput name="username" v-model="item.username" required>
              </ItpFormInput>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="9"
              name="agent_key"
              :label="pgettext('InvoicingProviderSettings', 'Agent key')"
              :helpTooltip="
                pgettext(
                  'InvoicingProviderSettings',
                  'The technical user\'s agent key.'
                )
              "
              required
            >
              <ItpFormInput name="agent_key" v-model="item.agent_key" required>
              </ItpFormInput>
            </ItpFormGroup>
          </ItpCol>
        </ItpRow>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :form="forms[formName] && forms[formName].id"
          :text="pgettext('Button', 'Save')"
          type="submit"
          variant="primary"
          default
          :disabled="!forms[formName] || !forms[formName].submittable"
          :spinning="actions.submit && actions.submit.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="pgettext('Button', 'Close')"
          variant="default"
          :disabled="actions.submit && actions.submit.isRunning"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
